import { useId, useState } from 'react';
import { formatCompact } from './TrendChart';

/**
 * Sequential blue ramp, light → dark. Shared with `ClickHeatmap.tsx`, so "darker = more" is learned
 * once for the whole dashboard.
 *
 * ONE hue. A rainbow ramp for magnitude is the classic misread — the reader has to consult a legend
 * to know whether green is more than orange, where light-to-dark needs no lookup.
 *
 * VALIDATED, not chosen by eye. From the dataviz skill directory:
 *   node scripts/validate_palette.js "#8ab8f0,#5f9ae8,#3a7cd8,#2262b8,#164a92,#0e346c,#071f45" \
 *     --mode light --surface "#ffffff" --ordinal      → all four checks PASS
 *
 * The earlier ramp FAILED two checks, both visible on screen: its lightest data step sat at 1.32:1
 * against white (the low band was effectively invisible) and two mid steps differed by ΔL 0.047,
 * merging two magnitude bands into one.
 */
const RAMP = [
  '#eff6ff', // index 0, RESERVED for a true zero — deliberately below the ramp's contrast floor,
             // because "nothing happened here" should recede rather than compete with the data
  '#8ab8f0',
  '#5f9ae8',
  '#3a7cd8',
  '#2262b8',
  '#164a92',
  '#0e346c',
  '#071f45',
] as const;

export interface HeatmapCell {
  row: string;
  col: string;
  value: number;
}

interface HeatmapProps {
  rowKeys: string[];
  colKeys: string[];
  cells: HeatmapCell[];
  rowLabel?: (key: string) => string;
  colLabel?: (key: string) => string;
  metricLabel?: string;
}

/**
 * A 2-D magnitude grid.
 *
 * The method's named default for "compare magnitude in a grid", and the only form that answers
 * "when do people visit" — an hour-of-day total and a day-of-week total cannot be combined into
 * this, because neither contains the hour-within-day detail.
 *
 * Zero and missing are drawn differently on purpose: a quiet hour is a fact, an uncollected hour
 * is not, and colouring them identically would invent data.
 */
export function Heatmap({
  rowKeys,
  colKeys,
  cells,
  rowLabel = (k) => k,
  colLabel = (k) => k,
  metricLabel = 'sessions',
}: HeatmapProps) {
  const gradientId = useId();
  const [hover, setHover] = useState<HeatmapCell | null>(null);

  if (rowKeys.length === 0 || colKeys.length === 0) {
    return (
      <div className="empty-state t-body">
        No grid data yet.
        <div className="t-label subtle" style={{ marginTop: 4 }}>
          Needs the day × hour cross-tab, which GA4 collects from the next ingestion onward.
        </div>
      </div>
    );
  }

  const lookup = new Map(cells.map((c) => [`${c.row}${c.col}`, c.value]));
  const max = Math.max(...cells.map((c) => c.value), 1);

  /** Bucket into the ramp. Index 0 is reserved for a true zero. */
  const shade = (value: number | undefined): string => {
    if (value === undefined) return 'transparent';
    if (value === 0) return RAMP[0]!;
    const step = Math.ceil((value / max) * (RAMP.length - 1));
    return RAMP[Math.min(RAMP.length - 1, Math.max(1, step))]!;
  };

  return (
    <div className="heatmap-wrap">
      <div className="heatmap-scroll">
        <table className="heatmap" role="table">
          <thead>
            <tr>
              <th className="heatmap-corner" />
              {colKeys.map((c) => (
                <th key={c} className="heatmap-colhead t-caption">{colLabel(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowKeys.map((r) => (
              <tr key={r}>
                <th className="heatmap-rowhead t-label">{rowLabel(r)}</th>
                {colKeys.map((c) => {
                  const value = lookup.get(`${r}${c}`);
                  return (
                    <td key={c} className="heatmap-cellwrap">
                      <div
                        className={`heatmap-cell${value === undefined ? ' heatmap-cell-missing' : ''}`}
                        style={{ background: shade(value) }}
                        // The hit area is the padded wrapper, not the 14px square — a target you
                        // must land on dead-centre reads as broken.
                        onMouseEnter={() => setHover({ row: r, col: c, value: value ?? 0 })}
                        onMouseLeave={() => setHover(null)}
                        tabIndex={0}
                        onFocus={() => setHover({ row: r, col: c, value: value ?? 0 })}
                        onBlur={() => setHover(null)}
                        aria-label={`${rowLabel(r)} ${colLabel(c)}: ${value ?? 'no data'}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="heatmap-footer">
        <div className="heatmap-legend" aria-hidden="true">
          <span className="t-caption muted">less</span>
          {RAMP.map((c) => (
            <span key={c} className="heatmap-swatch" style={{ background: c }} />
          ))}
          <span className="t-caption muted">more</span>
          <span className="t-caption subtle" style={{ marginInlineStart: 8 }}>
            {metricLabel} · max {formatCompact(max)}
          </span>
        </div>

        {/* Tooltips enhance, never gate: the same values are reachable from the table view. */}
        <div className="heatmap-readout t-label" id={gradientId} role="status">
          {hover
            ? `${rowLabel(hover.row)} · ${colLabel(hover.col)} — ${formatCompact(hover.value)} ${metricLabel}`
            : 'Hover a cell for detail'}
        </div>
      </div>
    </div>
  );
}

/** GA4 returns dayOfWeek as "0".."6" with 0 = Sunday. */
export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function dayLabel(key: string): string {
  const n = Number(key);
  return Number.isInteger(n) && DAY_LABELS[n] ? DAY_LABELS[n]! : key;
}

/** Label only every third hour — 24 labels on one axis collide at any realistic width. */
export function hourLabel(key: string): string {
  const n = Number(key);
  if (!Number.isInteger(n)) return key;
  return n % 3 === 0 ? `${n}` : '';
}
